// A death report: what THIS client saw when its own worm died, sent to the host
// and kept in the match's replay (edge-server replay/death-reports.js).
//
// The replay holds the host's truth: where it thinks the worm died and what it
// hit. When a player says "I died in the open", the question is what THEIR
// screen and THEIR collision grid held at that moment, and only this client knows:
//   - who called the death (our local collision, or the host's verdict arriving),
//   - what our collision hit, if anything (local-collision.ts getCrash),
//   - our clocks: how far ahead of the host's view our worm ran, how far behind
//     the opponents were drawn, and how stale each opponent's data was,
//   - the last seconds of our predicted path, BEFORE a server death snaps it back,
//   - how evenly the host's updates were arriving (a stall is a prime suspect).
// Small on purpose (a few KB): it rides the game socket mid-round, and the host
// drops anything big (replay/death-reports.js).

export const DEATH_REPORT_VERSION = 1;
const TAIL_SECONDS = 3;
const TAIL_STEP = 0.03;

export interface DeathReportInput {
  gameState: any;
  playerId: string;
  wormId: any;
  trackId: string | null;
  calledBy: "client" | "server";
  round: number;
  local: any;          // predictor.crashInfo(wormId), null when we never saw a crash
  serverEvent?: any;   // the host's worm_died event, when it called the death
  clocks: { ownNow: number; serverNow: number; renderTime: number; oneWay: number; renderDelay: number };
  net: { maxUpdateGapMs: number; lastUpdateAgeMs: number };
}

const r3 = (v: any) => (typeof v === "number" ? Math.round(v * 1000) / 1000 : v);

export function buildDeathReport(input: DeathReportInput) {
  const { gameState, trackId, clocks } = input;
  const tracks = (gameState && gameState.wormPathSegments) || {};
  const own = (trackId && tracks[trackId]) || [];
  const last = own[own.length - 1];
  // The path as points, not segments: our own track is one tiny segment per frame
  // (hundreds in three seconds, ~80KB as segments), and a viewer only draws a line.
  // [t, x, y, jumping], one per TAIL_STEP of game time.
  const tail: number[][] = [];
  for (const s of own) {
    if (s.endTime < clocks.ownNow - TAIL_SECONDS) continue;
    const prev = tail[tail.length - 1];
    if (prev && s.endTime - prev[0] < TAIL_STEP && s !== last) continue;
    tail.push([r3(s.endTime), Math.round(s.endX * 10) / 10, Math.round(s.endY * 10) / 10, s.jump ? 1 : 0]);
  }
  // Every other worm: how fresh our data for it was, and whether it had ended.
  const others: any[] = [];
  for (const id of Object.keys(tracks)) {
    if (id === trackId || id.indexOf("#") !== -1) continue;
    const segs = tracks[id];
    const l = segs && segs[segs.length - 1];
    if (!l) continue;
    others.push({ trackId: id, latestEnd: r3(l.endTime), behindOwn: r3(clocks.ownNow - l.endTime), ended: l.type === "worm_died" });
  }
  return {
    v: DEATH_REPORT_VERSION,
    calledBy: input.calledBy,
    round: input.round,
    playerId: input.playerId,
    wormId: input.wormId,
    trackId,
    local: input.local || null,
    serverEvent: input.serverEvent || null,
    clocks: {
      ownNow: r3(clocks.ownNow), serverNow: r3(clocks.serverNow), renderTime: r3(clocks.renderTime),
      oneWay: r3(clocks.oneWay), renderDelay: r3(clocks.renderDelay),
      aheadOfServer: r3(clocks.ownNow - clocks.serverNow),
    },
    net: { maxUpdateGapMs: Math.round(input.net.maxUpdateGapMs), lastUpdateAgeMs: Math.round(input.net.lastUpdateAgeMs) },
    head: last ? { x: r3(last.endX), y: r3(last.endY), t: r3(last.endTime), size: last.size, jump: !!last.jump } : null,
    tail,
    others,
    page: { visible: typeof document !== "undefined" ? document.visibilityState === "visible" : true },
  };
}
